import {$window, $document, $body, APP_NAME} from '../utils/environment';
import AbstractModule from './AbstractModule';
import {Event as ScrollEvent} from './LocomotiveScroll';

const DATA_KEY = `${APP_NAME}.Form`;
const EVENT_KEY = `.${DATA_KEY}`;

export const Event = {
    CLICK:        `click${EVENT_KEY}`,
    FOCUSIN:      `focusin${EVENT_KEY}`,
    FOCUSOUT:     `focusout${EVENT_KEY}`,
    INPUT:        `input${EVENT_KEY}`,
    CHANGE:       `change${EVENT_KEY}`,
    SUBMIT:       `submit${EVENT_KEY}`,
    TRACK_FOCUS:  `focus.track${EVENT_KEY}`
};

const Class = {
    ERROR:     '-error',
    REQUIRED:  '-is-required'
};

const Selector = {
    UPDATE_PASSWORD:  '.js-update-password',
    FIELDSET:         '.js-fieldset',
    FORM:             '.js-form',
    ERRORABLE:        '.-errorable',
    INPUT:            'input,textarea',
    SELECT:           '.js-selectric',
    PASSWORD:         '.js-input-password',
    PASSWORD2:        '.js-input-password2',
    PASSWORD_RESET:   '.js-input-password-reset',
    SUBMIT:           '.js-submit',
    ERROR_MSG:        '.js-global-error-msg',
    FILES:            '.js-form-file'
};

export default class extends AbstractModule {
    /**
     * Constructor.
     *
     * @return {void}
     */
    constructor(options) {
        super(options);

        this.captchaId = null;

        this.isTransmitting = false;
        this.captchaInitialized = false;
        this.contentType = 'application/x-www-form-urlencoded; charset=UTF-8';
        this.processData = true;
        this.hasFormdata = false;
        this.usingFormdata = false;
        this.$form = this.$el.find(Selector.FORM);
        this.$fieldsets = this.$el.find(Selector.FIELDSET);
        this.$inputs = this.$el.find(Selector.INPUT);
        this.$selects = this.$el.find(Selector.SELECT);
        this.$files = this.$el.find(Selector.FILES);
        this.$password = this.$el.find(Selector.PASSWORD);
        this.$passwordValidation = this.$el.find(Selector.PASSWORD2);
        this.$globalErrorMsg = this.$el.find(Selector.ERROR_MSG);
        this.$errors = this.$el.find('.js-errors');
        this.$submit = this.$el.find(Selector.SUBMIT);
        this.$preSubmit = this.$el.find('.js-pre-submit');
        this.$postSubmit = this.$el.find('.js-post-submit');
        this.$captchas = this.$el.find('.js-captcha');
        this.closeTimer = options.close_timer;
        this.captcha = typeof options.captcha !== 'undefined' ? options.captcha : true;
        this.event = options.event;
        this.resetTimer = options.reset_timer;

        // additional
        let additionalValidation = options.additional_validation;
        if (additionalValidation) {
            this.$inputs.push($(`.${additionalValidation}`).find(Selector.INPUT));
        }
    }

    /**
     * Initialize dom and events.
     *
     * @return {void}
     */
    init() {
        // Use FormData only if file inputs are found
        if (this.$inputs.filter('[type="file"]').length > 0) {
            this.contentType = false;
            this.processData = false;
            this.usingFormdata = true;
        }

        this.$el.on(Event.CLICK, Selector.UPDATE_PASSWORD, event => this.onChangePassword(event));
        $document.on(Event.FOCUSIN, this.$inputs, event => this.onFocusInFieldset(event));
        $document.on(Event.FOCUSOUT, this.$inputs, event => this.onFocusOutFieldset(event));
        $document.on(Event.INPUT, this.$inputs, event => this.onRemoveError(event));
        $document.on(Event.CHANGE, this.$inputs, event => this.onRemoveError(event));
        this.$el.on(Event.CHANGE, Selector.SELECT, event => this.onRemoveError(event));
        this.$form.on(Event.SUBMIT, event => this.onPreSubmit(event));

        $(Selector.INPUT).each((index, ele) => {
            if ($(ele).val() || $(ele).html()) {
                $(ele).parent().addClass('is-field-typed');
            }
        });
        $(Selector.SELECT).each((index, ele) => {
            if ($(ele).val()) {
                $(ele).parent().addClass('is-field-typed');
            }
        });
    }

    /**
     * Événement pour pouvoir modifier le mot
     * de passe.
     *
     * @method onChangePassword
     * @param  {Event}         event événement `click`
     * @return {void}
     */
    onChangePassword(event) {
        event.preventDefault();

        // hide 'change password' button
        this.$el.find(Selector.UPDATE_PASSWORD)
            .parent().addClass('is-hidden');

        // display change password form
        this.$el.find(Selector.PASSWORD)
            .parent()
            .removeClass('is-hidden');
        this.$el.find(Selector.PASSWORD)
            .children()
            .removeAttr('disabled');
        this.$el.find(Selector.PASSWORD2)
            .parent()
            .removeClass('is-hidden');
        this.$el.find(Selector.PASSWORD2)
            .children()
            .removeAttr('disabled');
        this.$el.find(Selector.PASSWORD_RESET)
            .children()
            .removeAttr('disabled');
    }

    /**
     * Événement lorsque le curseur est présent
     * dans le champ de saisie du fieldset.
     *
     * @method onFocusInFieldset
     * @param  {Event}         event événement `focusin`
     * @return {void}
     */
    onFocusInFieldset(event) {
        event.preventDefault();
        $(event.target).parents(Selector.FIELDSET).addClass('is-field-focused is-field-typed');

        this.initRecaptcha();
    }

    /**
     * Événement lorsque le curseur est en dehors
     * du champ de saisie du fieldset.
     *
     * @method onFocusOutFieldset
     * @param  {Event}         event événement `focusout`
     * @return {void}
     */
    onFocusOutFieldset(event) {
        event.preventDefault();

        if (!$(event.target).val()) {
            $(event.target).parents(Selector.FIELDSET).removeClass('is-field-typed');
        }

        if ($(event.target).prop('tagName') === 'textarea' && !$(event.target).html()) {
            $(event.target).parents(Selector.FIELDSET).removeClass('is-field-typed');
        }

        $(event.target).parents(Selector.FIELDSET).removeClass('is-field-focused');
    }

    /**
     * Événement pour supprimer la classe `-error`
     * dans le fieldset lors d'un changement du contenu.
     *
     * @method onRemoveError
     * @param  {Event}      event événement `input`
     * @return {void}
     */
    onRemoveError(event) {
        event.preventDefault();

        $(event.target).closest(Selector.ERRORABLE, this.$el).removeClass(Class.ERROR);
    }

    /**
     * Submits the form data to an URL with $.ajax
     *
     * @return {void}
     */
    submit() {
        const data = (this.usingFormdata) ? new FormData(this.$form[0]) : this.$form.serializeArray();

        let xhrRequest;

        if (!this.isTransmitting) {
            this.toggleLoading();
            this.$submit.toggleClass('is-loading');
            this.$form.find(`.${Class.ERROR}`).removeClass(Class.ERROR);
            this.$errors.slideUp();

            setTimeout(() => {
                xhrRequest = $.ajax({
                    method:      (this.$form.prop('method') || 'POST'),
                    url:         (this.$form.prop('action')),
                    contentType: this.contentType,
                    processData: this.processData,
                    data:        data,
                    dataType:    'json'
                })
                .done((response, textStatus) => {
                    if (response.success === true) {
                        this.$preSubmit.fadeOut(() => {
                            this.$form.trigger('reset');
                            this.$fieldsets.prop('disabled', false);

                            // if (this.$el.data('form-email')) {
                            //     const email = this.$el.find('.js-form-input-email').val();
                            //     this.$el.find('.js-form-feedback-email').text(email);
                            // }

                            this.$postSubmit.fadeIn(() => {
                                $('.js-scroll').triggerHandler('update.LocomotiveScroll');
                                $('.js-scroll').triggerHandler({
                                    type:    'scrollTo.LocomotiveScroll',
                                    options: {
                                        targetElem:   this.$postSubmit,
                                        targetOffset: -500
                                    }
                                });
                            });
                        });

                        if (typeof(this.resetTimer) !== 'undefined') {
                            setTimeout(() => {
                                this.reset();
                            }, this.resetTimer)
                        }

                        if (typeof(this.event) !== 'undefined') {
                            $document.triggerHandler({
                                type:     this.event,
                                response: response
                            });
                        }

                        // handles redirection
                        if (typeof(response.next_url) !== 'undefined') {
                            $document.triggerHandler({
                                type:    `goto.${APP_NAME}.TransitionManager`,
                                options: {
                                    transition: 'default',
                                    location:   response.next_url
                                }
                            });
                        }
                    } else {
                        this.manageErrors([
                            {
                                global: 'Something went wrong. We apologize for the inconvenience caused. Please try again or contact us directly.'
                            }
                        ]);

                        console.warn(
                            '[Form#submit~done]',
                            'Something went wrong:',
                            textStatus,
                            response
                        );
                    }
                })
                .fail((jqXHR, textStatus) => {
                    const response = jqXHR.responseJSON;

                    if (response && response.errors) {
                        if (!Array.isArray(response.errors)) {
                            response.errors = [ response.errors ];
                        }

                        if (response.errors.length > 0) {
                            this.manageErrors(response.errors);
                            return;
                        }
                    }

                    this.manageErrors([
                        {
                            global: 'Something went wrong. We apologize for the inconvenience caused. Please try again or contact us directly.'
                        }
                    ]);

                    console.warn(
                        '[Form#submit~fail]',
                        'Something went wrong:',
                        textStatus,
                        (jqXHR.responseJSON || jqXHR.responseXML || jqXHR.responseText || 'UNKNOWN')
                    );
                })
                .always(() => {
                    if (typeof(window.grecaptcha) !== 'undefined' && this.captchaId !== null) {
                        window.grecaptcha.reset(this.captchaId);
                    }
                    this.$submit.toggleClass('is-loading');
                    $document.triggerHandler('scrollTop.HeaderNavigation');
                    this.toggleLoading();
                });
            }, 600);
        }
    }

    /**
     * Check errors and exec the recaptha
     * @param  {object} submit event
     * @return @void
     */
    onPreSubmit(event) {
        event.preventDefault();
        event.stopPropagation();

        this.$globalErrorMsg.html('').slideUp();
        let hasErrors = false;
        let firstField = null;

        if (this.$form.data('prevent-submission')) {
            return
        }

        this.$inputs.each((index, ele) => {
            // required field
            if (
                $(ele).hasClass(Class.REQUIRED) &&
                $(ele).attr('disabled') !== 'disabled'
            ) {
                let hasValue = false;

                // is checkbox
                switch ($(ele).attr('type')) {
                    case 'checkbox':
                        hasValue = !!$(ele).is(':checked');
                        break;
                    default:
                        hasValue = !!$(ele).val().length;
                }

                if (!hasValue) {
                    const $field = $(ele).closest(Selector.ERRORABLE, this.$el);
                    if ($field && $field.length) {
                        $field.addClass(Class.ERROR);
                        $field.attr('data-error-msg', 'required');
                    }

                    hasErrors = true;

                    if (!firstField) {
                        firstField = $(ele);
                    }
                }
            }
        });

        this.$selects.each((index, ele) => {
            if (!$(ele).val() && $(ele).hasClass(Class.REQUIRED)) {
                $(ele).parents(Selector.FIELDSET).addClass(Class.ERROR);
                $(ele).parents('[data-error-msg]').attr('data-error-msg', 'required');

                hasErrors = true;
            }
        });

        if (hasErrors) {
            this.$globalErrorMsg.html('Some fields must be filled.').slideDown();
            this.$errors.slideDown();

            $('.js-scroll').triggerHandler({
                type:    'scrollTo.LocomotiveScroll',
                options: {
                    targetElem:   $(firstField),
                    targetOffset: -500
                }
            });

            $('.js-order').removeClass('is-next');

            firstField.focus();

            return null;
        }

        let emailRegex = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
        let phoneRegex = /^([0-9][-. ]?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
        let zipRegex = /^[ABCEGHJKLMNPRSTVXY]\d[A-Z] *\d[A-Z]\d$/i;

        this.$inputs.each((index, ele) => {
            if ($(ele).attr('disabled') === 'disabled' || !$(ele).attr('disabled')) {
                return;
            }

            if ($(ele).attr('type') === 'email') {
                let isValid = $(ele).val().match(emailRegex);

                if (!isValid) {
                    const $field = $(ele).closest(Selector.ERRORABLE, this.$el);
                    if ($field && $field.length) {
                        $field.addClass(Class.ERROR);
                        $field.attr('data-error-msg', 'Invalid email');
                    }

                    hasErrors = true;

                    if (!firstField) {
                        firstField = $(ele);
                    }
                }
            }

            if ($(ele).attr('type') === 'tel') {
                let isValid = $(ele).val().match(phoneRegex);

                if (!isValid) {
                    const $field = $(ele).closest(Selector.ERRORABLE, this.$el);
                    if ($field && $field.length) {
                        $field.addClass(Class.ERROR);
                        $field.attr('data-error-msg', 'Invalid phone number');
                    }

                    hasErrors = true;

                    if (!firstField) {
                        firstField = $(ele);
                    }
                }
            }

            if ($(ele).attr('name') === 'postal_code') {
                let isValid = $(ele).val().match(zipRegex);

                if (!isValid) {
                    const $field = $(ele).closest(Selector.ERRORABLE, this.$el);
                    if ($field && $field.length) {
                        $field.addClass(Class.ERROR);
                        $field.attr('data-error-msg', 'Invalid postal code');
                    }

                    hasErrors = true;

                    if (!firstField) {
                        firstField = $(ele);
                    }
                }
            }
        });

        if (hasErrors) {
            this.$globalErrorMsg.html('Some fields are invalid').slideDown();
            this.$errors.slideDown();

            $('.js-scroll').triggerHandler({
                type:    'scrollTo.LocomotiveScroll',
                options: {
                    targetElem:   $(firstField),
                    targetOffset: -500
                }
            });

            firstField.focus();

            return null;
        }

        let pass1 = $(this.$password.first());
        let pass2 = $(this.$passwordValidation.first());

        if (pass1.val() !== pass2.val()) {
            pass1.parents(Selector.FIELDSET).addClass(Class.ERROR);
            pass1.closest(Selector.ERRORABLE, this.$el).attr('data-error-msg', 'The passwords must be identical.');
            pass2.parents(Selector.FIELDSET).addClass(Class.ERROR);
            pass2.closest(Selector.ERRORABLE, this.$el).attr('data-error-msg', 'The passwords must be identical.');
            pass1.focus();
            hasErrors = true;
        }

        if (hasErrors) {
            this.$globalErrorMsg.html('The passwords are not identical.').slideDown();
            this.$errors.slideDown();
            return null;
        }

        if (!hasErrors) {
            if (this.captcha) {
                this.initRecaptcha();
                grecaptcha.execute(this.captchaId);
            } else {
                this.submit();
            }
        }
    }

    /**
     * Initialize recaptcha
     */
    initRecaptcha() {
        if (!this.captchaInitialized) {
            let captcha = $(this.$captchas.first());
            let key = captcha.attr('data-sitekey');

            if (captcha && key) {
                try {
                    this.captchaId = grecaptcha.render(captcha.get(0), {
                        sitekey:  key,
                        size:     'invisible',
                        callback: () => this.submit()
                    }, true);
                    this.$fieldsets.prop('disabled', false);
                    this.captchaInitialized = true;
                } catch (e) {
                    console.warn(e);
                }
            }
        }
    }

    /**
     * Manage errors sent by back end on front end
     *
     * @param  {array}  errors  List of error objects
     * @return void
     */
    manageErrors(errors) {
        // Reset captcha
        if (typeof(window.grecaptcha) !== 'undefined' && this.captchaId !== null) {
            window.grecaptcha.reset(this.captchaId);
        }

        this.$globalErrorMsg.html('Some fields have errors or are still not filled').slideDown();

        let i = 0;
        const len = errors.length;

        for (; i < len; i++) {
            if (errors[i].global) {
                this.$globalErrorMsg.html(errors[i].global).slideDown();

                $('.js-scroll').triggerHandler({
                    type:    'scrollTo.LocomotiveScroll',
                    options: {
                        targetElem:   this.$globalErrorMsg,
                        targetOffset: -250
                    }
                });
            }

            if (errors[i].error) {
                let $input;

                if (errors[i].selector) {
                    $input = this.$el.find(errors[i].selector);
                } else if (errors[i].property) {
                    $input = this.$el.find('[name="' + errors[i].property + '"]');
                }

                if ($input && $input.length) {
                    const $field = $input.closest(Selector.ERRORABLE, this.$el);

                    if ($field && $field.length) {
                        $field.addClass(Class.ERROR);
                        $field.attr('data-error-msg', errors[i].error);

                        if (i === 0) {
                            $input.focus();

                            $('.js-scroll').triggerHandler({
                                type:    'scrollTo.LocomotiveScroll',
                                options: {
                                    targetElem:   $input,
                                    targetOffset: -250
                                }
                            });
                        }
                    }
                }
            }
        }

        this.$errors.slideDown();
    }

    /**
     * Simple method for toggling animation states
     *
     * @return void
     */
    toggleLoading() {
        this.isTransmitting = !this.isTransmitting;
        this.$fieldsets.prop('disabled', this.isTransmitting);
        this.$el.toggleClass('is-form-state-loading');
    }

    reset() {
        setTimeout(() => {
            this.$postSubmit.fadeOut(() => {
                this.$selects.val('').removeAttr('selected'); // Reconstruct the plugin options box
                this.$form.trigger('reset');
                this.$preSubmit.fadeIn();
            });
        }, 500);
    }

    /**
     * Destructeur.
     *
     * @return {void}
     */
    destroy() {
        $window.off(EVENT_KEY);
        $document.off(EVENT_KEY);
        this.$el.off(EVENT_KEY);
    }
}
